import React from 'react'

/**
 * The head of a step in a multi-step screen.
 *
 * Three lines, smallest first: where you are, what is being asked, and what
 * the answer does. The order is the point — the eye reads down the left edge
 * and never has to cross the card to find out which step it is on, which is
 * what a position parked on the right-hand side used to cost.
 *
 * It replaced a stepper widget, and does the same job in a third of the
 * height: three steps with titles of their own do not need a band above them
 * repeating those titles as dots.
 *
 * Sticky by default. This block is the only thing on screen that says which
 * step you are on, and these steps scroll — a repository list runs well past
 * the fold, and losing the header at the top of it is losing the answer to
 * "what am I doing here?" exactly when the list stops being self-evident.
 */
const StepHeader = ({ step, totalSteps, title, children, onBack, sticky = true }: {
  step: number
  totalSteps: number
  title: string
  /** Renders a back link above everything else. Absent means there is nowhere
   * to go back to — which is a real state here, not an oversight: once the app
   * exists, walking back into the form offers a Create button that would make
   * a second one. */
  onBack?: () => void
  /** The line under the title. Prose on the first step; on later ones it is
   * what the step is operating on — the repository, the image — which is the
   * same slot answering the same question with a fact instead of a sentence. */
  children?: React.ReactNode
  sticky?: boolean
}) => (
  <div
    // `rounded-t` unconditionally, and that is not decoration: this paints its
    // own background, so square corners here cover the card's rounded ones and
    // the panel ends up with a flat top. It has to match whether the pinning
    // happens here or on a wrapper around it.
    className={`px-[1.875rem] [@media(max-width:639.98px)]:px-4 pt-7 [@media(max-width:639.98px)]:pt-4 pb-5 [@media(max-width:639.98px)]:pb-3 border-b border-card-line bg-dark-blue rounded-t-[10px] ${
      // `z-10` so the content scrolls UNDER it rather than through it.
      //
      // `sticky={false}` where the caller pins a larger block that CONTAINS
      // this one — two nested sticky elements pin at different offsets and
      // end up overlapping each other.
      sticky ? 'sticky top-0 z-10' : ''
    }`}
  >
    {/* Above the eyebrow, not inside the line below the title. Back is a way
        OUT of this step, and the three lines under it describe the step — a
        control that leaves belongs before them, not filed among them. */}
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        className="mb-3 -ml-1 px-1 py-0.5 rounded text-[0.8125rem] leading-140 text-light-blue hover:text-white transition-colors"
      >
        ← Back
      </button>
    )}
    <div className="text-[0.6875rem] font-bold uppercase tracking-[0.09em] text-text-faint leading-140">
      Step {step} of {totalSteps}
    </div>
    <h2 className="m-0 mt-2 font-sora text-lg font-semibold leading-140 text-white">
      {title}
    </h2>
    {children && (
      <div className="mt-1.5 text-[0.8125rem] leading-140 text-text-dim">
        {children}
      </div>
    )}
  </div>
)

export default StepHeader
